/**
 * Ikony modulů a dlaždic: jedna mapa pro celý dashboard.
 *
 * Kontrakt UI identity: každý modul má v module.json unikátní `ui.icon`
 * (kebab-case lucide jméno). Stejná jména používá start menu i Aplikace
 * (miniaplikace), takže druhá mapa nikde nevzniká.
 *
 * Unikátnost + členství hlídá CI gate (scripts/check-module-ui.py) — čte klíče
 * PŘÍMO z bloku ICON_MAP níže, drž formát `'jmeno': Komponenta,` jeden na řádek.
 */
import {
  Activity,
  AppWindow,
  Archive,
  Banknote,
  Bell as BellIcon,
  BookOpen,
  BookUser,
  Bot,
  Box,
  Brain,
  Bug,
  Cable,
  CalendarClock,
  Chrome,
  Cloud,
  Cpu,
  Database,
  DatabaseZap,
  FileCheck,
  FileCode,
  FileSearch,
  FileStack,
  FileText,
  Fingerprint,
  FolderSync,
  GitBranch,
  GitMerge,
  Globe,
  GraduationCap,
  Grid2x2,
  Hand,
  HardDrive,
  HardDriveDownload,
  HelpCircle,
  Home,
  Inbox,
  Key,
  KeyRound,
  Layers,
  LayoutDashboard,
  LayoutGrid,
  LibraryBig,
  ListTree,
  type LucideIcon,
  Mail,
  Mails,
  MailSearch,
  Map,
  MessageCircle,
  Monitor,
  Network,
  Package,
  Palette,
  Pickaxe,
  Printer,
  Puzzle,
  Radio,
  ScanText,
  Search,
  Server,
  Settings,
  Share2,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Signpost,
  SquareTerminal,
  Terminal,
  Trash2,
  UserCheck,
  UserCircle,
  Users,
  Vault,
  Wand2,
  Waypoints,
  Wifi,
  Workflow,
  Wrench,
  Zap,
} from 'lucide-react';
import { CodableIcon } from '../components/icons/CodableIcon';

export const ICON_MAP: Record<string, LucideIcon> = {
  'codable': CodableIcon,
  'activity': Activity,
  'app-window': AppWindow,
  'archive': Archive,
  'banknote': Banknote,
  'bell': BellIcon,
  'book-open': BookOpen,
  'book-user': BookUser,
  'bot': Bot,
  'box': Box,
  'brain': Brain,
  'bug': Bug,
  'cable': Cable,
  'calendar-clock': CalendarClock,
  'chrome': Chrome,
  'circle-stack': DatabaseZap,
  'cloud': Cloud,
  'cpu': Cpu,
  'database': Database,
  'file-check': FileCheck,
  'file-code': FileCode,
  'file-search': FileSearch,
  'file-stack': FileStack,
  'file-text': FileText,
  'fingerprint': Fingerprint,
  'folder-sync': FolderSync,
  'git-branch': GitBranch,
  'git-merge': GitMerge,
  'globe': Globe,
  'graduation-cap': GraduationCap,
  'grid-2x2': Grid2x2,
  'hand': Hand,
  'hard-drive': HardDrive,
  'hard-drive-download': HardDriveDownload,
  'home': Home,
  'inbox': Inbox,
  'key': Key,
  'key-round': KeyRound,
  'layers': Layers,
  'layout-dashboard': LayoutDashboard,
  'layout-grid': LayoutGrid,
  'library-big': LibraryBig,
  'list-tree': ListTree,
  'mail': Mail,
  'mail-search': MailSearch,
  'mails': Mails,
  'map': Map,
  'message-circle': MessageCircle,
  'monitor': Monitor,
  'network': Network,
  'package': Package,
  'palette': Palette,
  'pickaxe': Pickaxe,
  'printer': Printer,
  'puzzle': Puzzle,
  'radio': Radio,
  'scan-text': ScanText,
  'search': Search,
  'server': Server,
  'settings': Settings,
  'share-2': Share2,
  'shield': Shield,
  'shield-alert': ShieldAlert,
  'shield-check': ShieldCheck,
  'signpost': Signpost,
  'square-terminal': SquareTerminal,
  'terminal': Terminal,
  'trash-2': Trash2,
  'user-check': UserCheck,
  'user-circle': UserCircle,
  'users': Users,
  'vault': Vault,
  'wand-2': Wand2,
  'waypoints': Waypoints,
  'wifi': Wifi,
  'workflow': Workflow,
  'wrench': Wrench,
  'zap': Zap,
};

/** Obecná ikona aplikace, když nic konkrétnějšího není. */
export const GENERIC_APP_ICON = 'app-window';

export function hasIcon(name: string | null | undefined): name is string {
  return !!name && Object.prototype.hasOwnProperty.call(ICON_MAP, name);
}

// Neznámá ikona nesmí tiše splynout s ostatními (dřív fallback Server →
// půlka modulů vypadala stejně a nikdo si nevšiml). HelpCircle + warn = vidět.
const warnedIcons = new Set<string>();
export function getIcon(name?: string | null): LucideIcon {
  if (hasIcon(name)) return ICON_MAP[name]!;
  if (name && !warnedIcons.has(name)) {
    warnedIcons.add(name);
    console.warn(`[nav] Neznámá ikona '${name}' — chybí v ICON_MAP (kontrakt UI identity)`);
  }
  return name ? HelpCircle : Server;
}
